import { cn } from '@/utils/utils_client';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  `cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors 
  disabled:pointer-events-none disabled:opacity-25 
  hover-animation
  [&_svg]:pointer-events-none  
  outline-none focus:outline-none active:outline-none   focus-visible:outline-none 
`,
  {
    variants: {
      variant: {
        default: '                        bg-primary          text-primary-foreground     hover:bg-primary/90',
        destructive: '                    bg-destructive      text-destructive-foreground hover:bg-destructive/90',
        outline: `                border border-input                     text-foreground`,
        secondary: '                      bg-secondary        text-secondary-foreground   hover:bg-secondary/80',
        ghost: '                                                                          hover:bg-accent hover:text-accent-foreground',
        link: '                                               text-primary                hover:underline underline-offset-4',

        'outline-on-page': '      border         border-page-border      bg-page      text-page-foreground      hover:bg-bar',
        'outline-on-bar': '       border         border-bar-border       bg-bar       text-bar-foreground       hover:bg-component',
        'outline-on-component': ' border         border-component-border bg-component text-component-foreground hover:bg-component-item',

        'no-border-on-page': 'text-page-foreground hover:text-page-foreground-hover',
        'no-border-on-bar': 'text-bar-foreground hover:text-bar-foreground-hover',
        'no-border-on-component': 'text-component-foreground hover:text-component-foreground-hover',
      },
      size: {
        full: 'size-full',
        '3-full': 'w-3 h-full',
        '5-full': 'w-5 h-full',
        //6
        'square-6': 'h-6 w-6',
        //8
        'square-8': 'size-8',
        '8-full': 'w-8 h-full',
        'rect-8-3': 'w-8 h-3',
        '8-5': 'w-8 h-5',
        //10
        'rect-10-6': 'w-10 h-6',
        'rect-10-8': 'w-10 h-8',
        'rect-10-5': 'w-10 h-5',
        'square-10': 'h-10 w-10',
        //12
        'square-12': 'size-12',
        //full
        'full-3.5': 'w-full h-3.5',
        'full-4': 'w-full h-4',
        'full-5': 'w-full h-5',
        'full-6': 'w-full h-6',
        'full-8': 'w-full h-8',
        'w-full-h-10': 'w-full h-10',
        'size-full': 'size-full',
        'rect-12-8': 'w-12 h-8',
        'square-3': 'h-3 w-3',
        'square-4': 'size-4',
        'square-5': 'h-5 w-5',
        'square-15': 'size-15',
        sm: 'h-9 rounded-md px-3',
        icon: 'h-10 w-10',
        default: 'h-10 px-4 py-2',
        lg: 'h-11 rounded-md px-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface UI_PRIMITIVE__button_props extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const UI_PRIMITIVE__button = React.forwardRef<HTMLButtonElement, UI_PRIMITIVE__button_props>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
UI_PRIMITIVE__button.displayName = 'Button';

export { buttonVariants };
